import axiosClient from './axiosClient';

/**
 * Typed axios wrappers for `/api/admin/globals/collections`.
 *
 * Collections are task-reward collection templates — groupings of rewards
 * that tenants reuse per program year. The versioning contract mirrors the
 * other globals (tasks, adventures, cohorts): draft `current_version` →
 * publish → tenants sync. Keep this file in lockstep with the route
 * handlers — in particular the PATCH body's `base_version` + 409
 * `{error:'version_conflict', current_version}` shape.
 *
 * Simplest R6 global — flat identity fields + active flag. The richer
 * "what's in the collection" join surface lives on the tenant side under
 * task_reward_collection and is modeled separately when R7 picks it up.
 */

export interface CollectionHistoryRow {
  version: number;
  collection_name: string | null;
  description: string | null;
  platform_collection_id: string | number | null;
  active: boolean;
  change_summary: string | null;
  change_type: 'create' | 'update' | 'delete' | 'publish' | 'rollback';
  created_at: string;
  created_by: string | null;
}

export interface AdminCollectionRow {
  collection_code: string;
  collection_name: string | null;
  description: string | null;
  platform_collection_id: string | number | null;
  active: boolean;
  current_version: number;
  updated_at: string;
  updated_by: string | null;
  status: 'in_sync' | 'never_published' | 'unpublished';
}

export interface AdminCollectionDetail extends AdminCollectionRow {
  history: CollectionHistoryRow[];
}

export interface ListCollectionsResponse {
  rows: AdminCollectionRow[];
}

export interface ListCollectionsParams {
  sort?: 'collection_code' | 'collection_name' | 'updated_at' | 'current_version';
  dir?: 'asc' | 'desc';
  status?: 'in_sync' | 'never_published';
}

export interface CreateCollectionPayload {
  collection_code: string;
  collection_name: string;
  description?: string | null;
  change_summary?: string;
}

export interface UpdateCollectionPayload {
  base_version: number;
  collection_name?: string;
  description?: string | null;
  active?: boolean;
  change_summary?: string;
}

/* ------------------------------------------------------------------ */
/*  Publish + Rollback                                                  */
/* ------------------------------------------------------------------ */

export interface PublishCollectionEntityRequest {
  collection_code: string;
  base_version: number;
}

export interface PublishCollectionsRequest {
  entities: PublishCollectionEntityRequest[];
  batch_id?: string;
  change_summary?: string;
}

export interface PublishCollectionEntityResult {
  collection_code: string;
  ok: boolean;
  published_version?: number;
  error?: string;
  message?: string;
  current_version?: number;
}

export interface PublishCollectionsResponse {
  batch_id: string;
  results: PublishCollectionEntityResult[];
}

export interface RollbackCollectionPayload {
  target_version: number;
  base_version: number;
  change_summary?: string;
}

const base = '/api/admin/globals/collections';

export const adminCollectionApi = {
  list: async (
    params: ListCollectionsParams = {},
  ): Promise<AdminCollectionRow[]> => {
    const res = await axiosClient.get<ListCollectionsResponse>(base, { params });
    return res.data.rows;
  },

  get: async (code: string): Promise<AdminCollectionDetail> => {
    const res = await axiosClient.get<AdminCollectionDetail>(
      `${base}/${encodeURIComponent(code)}`,
    );
    return res.data;
  },

  create: async (payload: CreateCollectionPayload): Promise<AdminCollectionRow> => {
    const res = await axiosClient.post<AdminCollectionRow>(base, payload);
    return res.data;
  },

  update: async (
    code: string,
    payload: UpdateCollectionPayload,
  ): Promise<AdminCollectionRow> => {
    const res = await axiosClient.patch<AdminCollectionRow>(
      `${base}/${encodeURIComponent(code)}`,
      payload,
    );
    return res.data;
  },

  publish: async (
    payload: PublishCollectionsRequest,
  ): Promise<PublishCollectionsResponse> => {
    const res = await axiosClient.post<PublishCollectionsResponse>(
      `${base}/publish`,
      payload,
    );
    return res.data;
  },

  publishOne: async (
    code: string,
    base_version: number,
    options?: { batch_id?: string; change_summary?: string },
  ): Promise<PublishCollectionsResponse> => {
    return adminCollectionApi.publish({
      entities: [{ collection_code: code, base_version }],
      batch_id: options?.batch_id,
      change_summary: options?.change_summary,
    });
  },

  rollback: async (
    code: string,
    payload: RollbackCollectionPayload,
  ): Promise<AdminCollectionRow> => {
    const res = await axiosClient.post<AdminCollectionRow>(
      `${base}/${encodeURIComponent(code)}/rollback`,
      payload,
    );
    return res.data;
  },
};
